/**
 * Manages the scroll history for search and collection pages for returning the user to the previous scroll position
 */
class ScrollHistoryManager {
  /**
   * @type {string}
   */
  from = window.history.state?.from || ''

  /**
   * @type {number}
   */
  fromPage = window.history.state?.from_page || false

  /**
   * @type {string}
   */
  productHandle = window.history.state?.product || ''

  constructor() {
    this.initEventListeners()
  }

  /**
   * @returns {number}
   */
  get scrollY() {
    return window.scrollY
  }

  /**
   * @returns {number|undefined}
   */
  get scrollPosition() {
    return window.history.state?.scrollPosition
  }

  /**
   * Replaces the history state on page hide to store the scroll position
   * @param {PageTransitionEvent} e - The page transition event
   * @returns {void}
   */
  replaceStateOnPageHide = (e) => {
    if (e.persisted) {
      this.setHistoryState()
    }
  }

  /**
   * Scrolls to the previous scroll position on page show
   * @param {PageTransitionEvent} e - The page transition event
   * @returns {void}
   */
  scrollYOnPageShow = (e) => {
    if (e.persisted) {
      if (this.scrollPosition !== undefined) {
        window.scrollTo({ top: this.scrollPosition, behavior: 'instant' })
      }
      this.clearScrollHistory()
    }
  }

  /**
   * Clears the history state for the current page
   * @returns {void}
   */
  clearScrollHistory() {
    window.history.replaceState({}, '', window.location.href)
  }

  /**
   * Updates the history state with the current page information
   * This is used to restore the page parameter for the history scroll item to restore the correct page before Athos loads the results
   * @returns {void}
   */
  updateHistoryState() {
    const urlParams = new URLSearchParams(window.location.search)
    if (this.fromPage) {
      urlParams.set('page', this.fromPage.toString())
      this.setHistoryState(urlParams)
    }
  }

  /**
   * Sets the history state with the current scroll and page information
   * @param {URLSearchParams} [urlParams] - Optional URL parameters to include
   * @returns {void}
   */
  setHistoryState = (urlParams) => {
    const state = {
      scrollPosition: this.scrollY,
      from: this.from,
      from_page: this.fromPage,
      product: this.productHandle
    }
    const url = urlParams ? `${window.location.pathname}?${urlParams}` : window.location.href
    window.history.replaceState(state, '', url)
  }

  /**
   * Updates the history state with collection or search information
   * @param {string} pageType - The type of page ('collection' or 'search')
   * @param {Object} result - The result object containing page and handle information
   * @param {Object} [collection] - Optional collection object for collection pages
   * @param {string} [query] - Optional search query for search pages
   * @returns {void}
   */
  updateHistoryStateWithCollectionInfo = (pageType, result, collection, query) => {
    this.fromPage = result.page || 1
    this.productHandle = result.handle
    if (pageType === 'collection' && typeof collection !== 'undefined') {
      this.from = collection.handle
    } else if (pageType === 'search' && query) {
      this.from = `search:${query}`
    }
    this.setHistoryState()
  }

  /**
   * Initializes the event listeners for the scroll history manager for persisted (BFCache) events
   * @returns {void}
   */
  initEventListeners() {
    window.addEventListener('pagehide', this.replaceStateOnPageHide)
    window.addEventListener('pageshow', this.scrollYOnPageShow)
  }
}

export const scrollHistoryManager = new ScrollHistoryManager()
